import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function AddToHomeScreen() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canAutoInstall, setCanAutoInstall] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the prompt
    const dismissed = localStorage.getItem('addToHomeScreenDismissed');
    const lastShown = localStorage.getItem('addToHomeScreenLastShown');
    const now = Date.now();
    
    // Show prompt if never dismissed, or if it's been more than 7 days
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const shouldShow = !dismissed || (lastShown && (now - parseInt(lastShown)) > sevenDays);

    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else if (/windows phone/i.test(userAgent)) {
      setPlatform('windows-phone');
    } else if (/symbian|nokia|blackberry|series60|series40|sonyericsson|samsung/i.test(userAgent)) {
      setPlatform('symbian');
    } else if (/windows/i.test(userAgent)) {
      setPlatform('windows');
    } else if (/macintosh|mac os x/i.test(userAgent)) {
      setPlatform('mac');
    } else {
      setPlatform('other');
    }

    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone || 
                        document.referrer.includes('android-app://');
    
    if (shouldShow && !isStandalone) {
      // Delay showing the prompt by 2 seconds for better UX
      setTimeout(() => {
        setShowPrompt(true);
        localStorage.setItem('addToHomeScreenLastShown', now.toString());
      }, 2000);
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge, Samsung Internet, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanAutoInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      setShowPrompt(false);
      localStorage.setItem('addToHomeScreenDismissed', 'true');
      setDeferredPrompt(null);
      setCanAutoInstall(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setShowPrompt(false);
          localStorage.setItem('addToHomeScreenDismissed', 'true');
        }
      } catch (error) {
        console.error('Install error:', error);
      }
      setInstalling(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('addToHomeScreenDismissed', 'true');
  };

  const handleRemindLater = () => {
    setShowPrompt(false);
  };

  // Get simple instructions for platforms that don't support auto-install
  const getManualInstructions = () => {
    switch (platform) {
      case 'ios':
        return {
          icon: '🍎',
          browser: 'Safari',
          steps: [
            'Tap the Share button (□↑) at the bottom',
            'Scroll and tap "Add to Home Screen"',
            'Tap "Add" to confirm'
          ]
        };
      case 'android':
        return {
          icon: '🤖',
          browser: 'Chrome',
          steps: [
            'Tap the menu (⋮) in the top right',
            'Tap "Add to Home Screen"',
            'Tap "Add" to confirm'
          ]
        };
      case 'windows-phone':
        return {
          icon: '📱',
          browser: 'Edge',
          steps: [
            'Tap the menu (···) at the bottom',
            'Tap "Pin to Start"'
          ]
        };
      case 'symbian':
        return {
          icon: '📲',
          browser: 'Browser',
          steps: [
            'Open browser menu',
            'Select "Add Bookmark"',
            'Save to home screen if available'
          ]
        };
      default:
        return {
          icon: '🌐',
          browser: 'your browser',
          steps: [
            'Open browser menu (⋮ or ☰)',
            'Look for "Install" or "Add to Home Screen"',
            'Follow the prompts'
          ]
        };
    }
  };

  const instructions = getManualInstructions();

  if (!showPrompt) return null;

  return (
    <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
      <DialogContent className="bg-white max-w-sm mx-4 p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6 text-center">
          <div className="text-5xl mb-3">📲</div>
          <DialogTitle className="text-xl font-heading font-bold text-white">
            Add Excel Era to Home Screen
          </DialogTitle>
          <p className="text-sm text-white/80 mt-2">
            Quick access with one tap!
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* Benefits - compact */}
          <div className="flex justify-around text-center text-xs">
            <div className="flex flex-col items-center gap-1">
              <span className="text-2xl">⚡</span>
              <span className="text-muted-foreground">Fast Access</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-2xl">📱</span>
              <span className="text-muted-foreground">App-Like</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-2xl">🔔</span>
              <span className="text-muted-foreground">Quick Book</span>
            </div>
          </div>

          {/* Auto-install available */}
          {canAutoInstall ? (
            <div className="space-y-3">
              <Button
                onClick={handleInstall}
                disabled={installing}
                className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-lg"
              >
                {installing ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Installing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    CREATE SHORTCUT
                  </span>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Click above to instantly add to your home screen
              </p>
            </div>
          ) : (
            /* Manual instructions for iOS and unsupported browsers */
            <div className="space-y-3">
              <div className="bg-accent p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{instructions.icon}</span>
                  <span className="font-bold text-sm">Using {instructions.browser}:</span>
                </div>
                <ol className="space-y-2">
                  {instructions.steps.map((step, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <span className="flex-shrink-0 w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="text-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              
              {platform === 'ios' && (
                <p className="text-xs text-center text-amber-600 bg-amber-50 p-2 rounded">
                  ⚠️ iOS requires Safari browser for home screen apps
                </p>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleRemindLater}
              variant="outline"
              className="flex-1 h-10 text-sm"
            >
              Later
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="flex-1 h-10 text-sm text-muted-foreground"
            >
              Don't show
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
